import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArtworkRow, ArtworkRowSkeleton } from "./ArtworkRow";
import { ArtworkCard, ArtworkCardSkeleton } from "./ArtworkCard";
import { useViewMode } from "./useViewMode";
import { Spinner, StateMessage, Button } from "../../components/ui";
import { listStagger } from "../../motion";
import { useMediaRefresh } from "../media/useMediaRefresh";
import { useT } from "../../i18n/strings";
import { messagesOf } from "../../api/errors";
import type { ArtworkRow as Row } from "../../api/types";
import s from "./artworks.module.css";

interface ArtworkListProps {
  rows: Row[];
  isLoading: boolean;
  error: unknown;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  empty: React.ReactNode;
}

export function ArtworkList({
  rows,
  isLoading,
  error,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onRefresh,
  empty,
}: ArtworkListProps) {
  const t = useT();
  const view = useViewMode();
  const grid = view === "grid";
  const sentinel = useRef<HTMLDivElement>(null);
  // Rows share one cooldown: thirty expired thumbnails are still one refetch.
  const refreshMedia = useMediaRefresh(onRefresh);

  // Cursor pages arrive as the sentinel scrolls into view; no "load more" tap.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && onLoadMore(),
      { rootMargin: "320px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, rows.length]);

  if (isLoading) {
    return (
      <div className={grid ? s.grid : s.list} aria-busy="true">
        {Array.from({ length: grid ? 4 : 6 }, (_, index) =>
          grid ? <ArtworkCardSkeleton key={index} /> : <ArtworkRowSkeleton key={index} />,
        )}
      </div>
    );
  }

  if (error) {
    return (
      <StateMessage
        icon="alert"
        title={t.errors.title}
        body={messagesOf(error)[0]}
        action={
          <Button variant="secondary" onClick={onRefresh}>
            {t.actions.retry}
          </Button>
        }
      />
    );
  }

  if (!rows.length) return <>{empty}</>;

  return (
    <>
      <motion.div
        className={grid ? s.grid : s.list}
        variants={listStagger}
        initial="hidden"
        animate="show"
      >
        {/* popLayout so the rows that survive a filter close the gap the
            removed ones leave, instead of jumping into it. */}
        <AnimatePresence mode="popLayout" initial={false}>
          {rows.map((row) =>
            grid ? (
              <ArtworkCard key={row.id} artwork={row} onMediaError={refreshMedia} />
            ) : (
              <ArtworkRow key={row.id} artwork={row} onMediaError={refreshMedia} />
            ),
          )}
        </AnimatePresence>
      </motion.div>

      <div ref={sentinel} className={s.sentinel} />
      {isFetchingMore && (
        <div className={s.footLoad}>
          <Spinner />
        </div>
      )}
    </>
  );
}
